import {Trans} from '@lingui/macro';
import {DateTime, Duration} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../../components/common/boxedIcon';
import {StyledCellChildrenWrapperDiv} from '../../../components/common/table/cells/childrenCell';
import {TableCell, TableCellProps} from '../../../components/common/table/cells/tableCell';
import {getRuleTypeIcon, getRuleTypePaletteColor} from './ruleBundleCategoryStyles';
import {RuleListItem} from './ruleListHelpers';
import {RuleListRowActions} from './ruleListRow';

/*
 * Props.
 */

export interface RuleListCellNameProps
  extends TableCellProps<RuleListItem>,
    Pick<RuleListRowActions, 'onRuleEdit' | 'onRuleDelete'> {
  rule: RuleListItem;
}

/*
 * Styles.
 */

const StyledNameWrapper = styled.div`
  flex: 1;

  display: grid;
  grid-template-areas:
    'icon title'
    'icon description';

  grid-template-columns: 40px 1fr;
  grid-template-rows: 1fr 1fr;

  column-gap: 12px;
`;

const StyledTitle = styled.div<{$isSelected: boolean}>`
  grid-area: title;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  ${ellipsis()};
  line-height: 20px;

  color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade90)};
`;

const StyledDescription = styled.div<{$isSelected: boolean}>`
  grid-area: description;
  ${flexCenter};
  height: 16px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  ${ellipsis()};
  line-height: 16px;

  color: ${(p) => (p.$isSelected ? p.theme.alphas.white50 : p.theme.greys.shade70)};
`;

const StyledIconWrapper = styled.div`
  grid-area: icon;
`;

const StyledSimpleDropdown = styled(SimpleDropdown)`
  z-index: 1;
`;

const StyledRuleWarning = styled.span`
  color: ${(p) => p.theme.palette.orange.shade40};
  margin-left: 4px;
  margin-right: 4px;
`;

/*
 * Component.
 */

export const RuleNameCell: FC<RuleListCellNameProps> = ({
  rule: value,
  onRuleEdit,
  onRuleDelete,
  ...cellProps
}) => {
  const theme = useUserTheme();

  return (
    <TableCell
      {...cellProps}
      value={value}
      renderCell={(rule) => {
        const paletteColor = getRuleTypePaletteColor(rule.type);
        const icon = getRuleTypeIcon(rule.type);
        const isSelected = Boolean(cellProps.isSelected);

        return (
          <StyledCellChildrenWrapperDiv
            $settings={cellProps.settings}
            $isHovering={Boolean(cellProps.isHovering)}
            $index={cellProps.index}
            $isSelected={Boolean(cellProps.isSelected)}
            $shouldRemoveTopBorderRadius={cellProps.shouldRemoveTopBorderRadius}
            $shouldRemoveBottomBorderRadius={cellProps.shouldRemoveBottomBorderRadius}
            $hasPadding
          >
            <StyledNameWrapper>
              <StyledIconWrapper>
                <BoxedIcon
                  name={icon.regular.filled}
                  color={rule.type ? theme.palette[paletteColor].shade40 : theme.greys.shade70}
                  backgroundColor={theme.palette[paletteColor].shade20}
                />
              </StyledIconWrapper>
              <StyledTitle $isSelected={isSelected}>{rule.name}</StyledTitle>
              <StyledDescription $isSelected={isSelected}>{renderDescription(rule, theme)}</StyledDescription>
            </StyledNameWrapper>
            {!isSelected && cellProps.isHovering && (
              <DropdownBoxCoordinator
                renderDropdown={(popoverProps) =>
                  renderDropdown(
                    popoverProps,
                    () => onRuleEdit(rule),
                    () => onRuleDelete(rule),
                  )
                }
                renderButton={() => (
                  <IconButton label="" iconName="moreVertical" color={theme.greys.shade70} />
                )}
              />
            )}
          </StyledCellChildrenWrapperDiv>
        );
      }}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdown(popoverProps: PopoverTransferProps, onRuleEdit: Handler, onRuleDelete: Handler) {
  return (
    <StyledSimpleDropdown {...popoverProps}>
      <SimpleDropdownItem onSelect={() => onRuleEdit()}>
        <Trans id="rule-list-menu-rule-edit">Edit rule</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem onSelect={() => onRuleDelete()}>
        <Trans id="rule-list-menu-rule-delete">Delete rule</Trans>
      </SimpleDropdownItem>
    </StyledSimpleDropdown>
  );
}

const RULE_LAST_APPLIED_THRESHOLD = Duration.fromObject({months: 6});
function isPotentiallyInactive(rule: RuleListItem) {
  const lastApplied = DateTime.fromMillis(rule.lastApplied ?? 0);
  const createdAt = DateTime.fromMillis(rule.createdAt ?? 0);

  // Don't show the warning if the rule was created recently.
  if (createdAt.plus(RULE_LAST_APPLIED_THRESHOLD) > DateTime.now()) {
    return false;
  }

  return lastApplied.plus(RULE_LAST_APPLIED_THRESHOLD) < DateTime.now();
}

function maybeRenderWarning(rule: RuleListItem, theme: UserTheme) {
  if (isPotentiallyInactive(rule)) {
    return (
      <>
        <Icon name="warningFilledTiny" color={theme.palette.orange.shade40} />
        <StyledRuleWarning>
          <Trans id="rule-list-applies-more-than-threshold">Last applied more than 6 months ago</Trans>
        </StyledRuleWarning>
      </>
    );
  }

  return null;
}

function renderDescription(rule: RuleListItem, theme: UserTheme) {
  const description = rule.description || null;
  const warning = maybeRenderWarning(rule, theme);

  return (
    <>
      {warning}
      {Boolean(description && warning) && ' · '}
      {description}
    </>
  );
}
